import React, { useContext, useEffect, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';

import { SearchTextContext, SearchPaneResultsContext } from 'common/contexts';
import { ADVANCED_OPTIONS_PARAMS, COMPARATOR_API_MAP, EQUAL } from 'common/constants/modifiers';
import { simpleFetch } from 'common/utils/http';
import useAttributeFilters from 'common/customHooks/useAttributeFilters';

import { Modal } from 'antd';
import SearchPaneView from './SearchPaneView';
import { EditUserContext, SelectedUsersContext } from '../../context';
import { orderSearchResults } from './searchUtils';
import { DefaultAdvancedOptions } from '../../../../common/constants/types';

const filterSearchResults = searchResult =>
  _filter(searchResult, result => !_isEmpty(result.genres));

// construct query param for each filter that has modifiers. e.g. name=eq:John, name=ne:derived_only:John
const constructQueryParam = filter => {
  let query = `${encodeURIComponent(filter.name)}=`;
  if (filter.comparator !== EQUAL) {
    query = query.concat(`${COMPARATOR_API_MAP[filter.comparator]}:`);
  }

  if (filter.advancedOptions && filter.advancedOptions !== DefaultAdvancedOptions) {
    if (ADVANCED_OPTIONS_PARAMS.has(filter.advancedOptions.derived)) {
      query = query.concat(`${filter.advancedOptions.derived}:`);
    }
    if (ADVANCED_OPTIONS_PARAMS.has(filter.advancedOptions.secondary)) {
      query = query.concat(`${filter.advancedOptions.secondary}:`);
    }
  }
  query = query.concat(encodeURIComponent(filter.filteredValues));
  return query;
};

const SearchPane = () => {
  const { sortedSearchResults, setSortedSearchResults } = useContext(SearchPaneResultsContext);
  const { isFiltering, attributeFilters } = useAttributeFilters();
  const { setEditUser } = useContext(EditUserContext);
  const { searchText } = useContext(SearchTextContext);
  const { setSelectedUsers } = useContext(SelectedUsersContext);
  const [openEmptySearchModal, setOpenEmptySearchModal] = useState(false);

  const toggleEmptySearchModal = () => setOpenEmptySearchModal(!openEmptySearchModal);

  useEffect(() => {
    // For text search, preserve the backend's relevance ranking (name/phone
    // matches first). For attribute filtering there's no relevance signal, so
    // fall back to alphabetical-by-name ordering.
    const showResults = (searchResults, { preserveOrder = false } = {}) => {
      const filteredSearchResults = filterSearchResults(searchResults);
      const orderedSearchResults = preserveOrder
        ? filteredSearchResults
        : orderSearchResults(filteredSearchResults);
      setSortedSearchResults(orderedSearchResults);
      setSelectedUsers([orderedSearchResults[0].person_id]);
    };

    const fetchResult = async () => {
      try {
        if (!_isEmpty(searchText)) {
          const searchResult = await simpleFetch(
            // SearchPane only needs everyone's name and phone number
            `/api/v1/people/search/${searchText}?fields=name,cell_phone`
          );

          if (!_isEmpty(searchResult) && !searchResult.error) showResults(searchResult, { preserveOrder: true });

        } else if (isFiltering) {
          const query = attributeFilters.map(filter => constructQueryParam(filter)).join('&');
          if (query) {
            const searchResult = await simpleFetch(`/api/v1/list?${query}&fields=name,cell_phone`);
            if (!searchResult) {
              setSelectedUsers([]);
              setSortedSearchResults([]);
            }
            if (!_isEmpty(searchResult) && !searchResult.error) {
              showResults(searchResult);
            }
          } else {
            setSelectedUsers([]);
            setSortedSearchResults([]);
          }
        } else {
          // default to displaying the logged in user in search pane
          const currentUserId = (await simpleFetch('/api/v1/current-user')).user_id;
          const currentUser = await simpleFetch(`/api/v1/people/${currentUserId}`);
          if (!_isEmpty(currentUser)) {
            setSelectedUsers([currentUserId]);
          } else {
            // show empty middle pane if results come back empty
            setSelectedUsers([]);
          }

          setSortedSearchResults([currentUser]);
        }
      } catch (e) {
        // search not found exception
        console.error(e);
        toggleEmptySearchModal();
      }
    };

    fetchResult();
    setEditUser(false);
  }, [searchText, attributeFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SearchPaneView searchResults={sortedSearchResults} />
      <Modal
        onCancel={toggleEmptySearchModal}
        onOk={toggleEmptySearchModal}
        open={openEmptySearchModal}
      >
        Sorry, there were no result found. Please update your search.
      </Modal>
    </>
  );
};

export default SearchPane;
